import React, { useContext, useState } from 'react'
import { Button } from '@/components/ui/button'
import { DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import {
  GridColumn as Column,
  Grid,
  GridCellProps,
  GridItemChangeEvent,
} from '@progress/kendo-react-grid'
import { Upload, UploadFileInfo, UploadOnAddEvent } from '@progress/kendo-react-upload'
import { CreatePromoMaterialForLandingPage } from 'api/ClientArea/CreatePromoMaterials'
import { LandingPageDto } from 'api/ClientArea/LandingPageDto'
import { PreviewPromoMaterialLandingPage } from 'api/ClientArea/PreviewPromoMaterials'
import { NameDto } from 'api/Common/Types'
import MoreButton from 'components/MoreButton/MoreButton'
import { Option, Select } from 'components/Select'
import { toBase64 } from 'utils/file.helpers'

import { errorToast, ToastContext } from '../../../components/Toast/Toast'

interface FileInfo extends UploadFileInfo {
  languageId: string
  promoMaterialCategoryId: number
  promoMaterialTypeId: number
  promoMaterial: string
  fileSize: string
  inEdit: boolean
  promoMaterialSize: NameDto
  promoMaterialCategory: NameDto
  promoMaterialType: NameDto
}

interface FileInfoUpload extends Omit<FileInfo, 'getRawFile'> {
  getRawFile(): File
}

interface FileInfo extends UploadFileInfo {
  languageId: string
  promoMaterialCategoryId: number
  promoMaterialTypeId: number
  promoMaterial: string
  inEdit: boolean
}

interface OuterProps {
  data: LandingPageDto
  promoMaterialsPreview: PreviewPromoMaterialLandingPage[]
  onSubmit(data: FileInfo[]): Promise<void>
  onPromoMaterialValidate(
    values: Omit<CreatePromoMaterialForLandingPage, 'id'>[]
  ): Promise<PreviewPromoMaterialLandingPage[] | undefined>
}

export const RelatedPromoMaterialUploadTable: React.FC<OuterProps> = (props) => {
  const { data, onSubmit, onPromoMaterialValidate } = props

  const setToast = useContext(ToastContext)

  const [isLoading, setLoading] = useState<boolean>(false)
  const [isPreviewValidated, setPreviewValidated] = useState<boolean>(false)
  const [promoMaterialFiles, setPromoMaterialFiles] = useState<FileInfo[]>([])

  const handleUpload = async () => {
    if (promoMaterialFiles?.length >= 1) {
      try {
        setLoading(true)
        await onSubmit(promoMaterialFiles)
      } finally {
        setLoading(false)
      }
    }
  }

  const handleAdd = async (event: UploadOnAddEvent): Promise<void> => {
    const files = [...promoMaterialFiles, ...event.newState]
    const edit = await Promise.all(
      files
        .filter((x): x is FileInfoUpload => !!x?.getRawFile)
        .map(async (x) => {
          const base64 = await toBase64(x.getRawFile())
          const promoMaterial = typeof base64 === 'string' ? base64 : ''
          return {
            ...x,
            id: null,
            inEdit: true,
            promoMaterial,
          }
        })
    )
    setPromoMaterialFiles(edit)
    setPreviewValidated(false)
  }

  const handleUploadValidation = async () => {
    if (promoMaterialFiles?.length >= 1) {
      try {
        setLoading(true)

        const newPromoMaterialFiles: Omit<CreatePromoMaterialForLandingPage, 'id'>[] =
          promoMaterialFiles.map((x) => ({
            name: x.name,
            promoMaterial: x.promoMaterial,
            languageId: x.languageId,
            endDateTime: null,
            isEnabled: true,
          }))

        const promoMaterialsPreview = await onPromoMaterialValidate(newPromoMaterialFiles)

        if (promoMaterialsPreview) {
          const previewPromoMaterials = promoMaterialFiles
            .filter((_, index) => !!promoMaterialsPreview[index])
            .map((x, index) => {
              const promoMaterialPreview = promoMaterialsPreview[index]
              return {
                ...x,
                promoMaterialSize: promoMaterialPreview.promoMaterialSize,
                promoMaterialType: promoMaterialPreview.promoMaterialType,
                promoMaterialCategory: promoMaterialPreview.promoMaterialCategory,
              }
            })

          if (newPromoMaterialFiles.length === previewPromoMaterials.length) {
            setPromoMaterialFiles(previewPromoMaterials)
            setPreviewValidated(true)
          }
        }
      } catch (e) {
        console.error(e)
        setPreviewValidated(false)
      } finally {
        setLoading(false)
      }
    }
  }

  const handleChange = (event: GridItemChangeEvent): void => {
    if (event.field) {
      event.dataItem[event.field] = event.value
      setPromoMaterialFiles([...promoMaterialFiles])
      setPreviewValidated(false)
    }
  }

  const handleRemove = (uid: string): void => {
    const files = promoMaterialFiles.filter((x) => x.uid !== uid)
    setPromoMaterialFiles(files)
    setPreviewValidated(false)
  }

  const handleGridChange =
    (gridCellProps: GridCellProps) => (value: React.SyntheticEvent<HTMLInputElement>) => {
      if (gridCellProps.onChange) {
        gridCellProps.onChange({
          ...gridCellProps,
          value,
          syntheticEvent: value,
        })
        setPreviewValidated(false)
      }
    }

  const isValid = (): boolean => {
    return promoMaterialFiles?.every(
      (x: FileInfo) => x?.languageId && x?.promoMaterial && x?.name?.length >= 3
    )
  }

  return (
    <DialogContent className='max-w-fit w-auto'>
      <DialogHeader>
        <DialogTitle>Bulk Promo Material Upload</DialogTitle>
      </DialogHeader>

      <Upload batch={false} autoUpload={false} multiple showFileList onAdd={handleAdd} />
      <Grid data={promoMaterialFiles} editField='inEdit' onItemChange={handleChange} resizable>
        <Column field='name' title='Name' width={250} />
        {isPreviewValidated && (
          <Column
            field='promoMaterialCategory.name'
            title='Category'
            width={150}
            editable={false}
          />
        )}
        {isPreviewValidated && (
          <Column field='promoMaterialType.name' title='Type' width={150} editable={false} />
        )}
        <Column
          field='languageId'
          title='Language'
          cell={(gridCellProps) => (
            <td>
              <Select
                value={
                  gridCellProps?.field
                    ? gridCellProps.dataItem[gridCellProps.field]?.toString()
                    : ''
                }
                onChange={handleGridChange(gridCellProps)}
                fullWidth
              >
                {(data.languages || []).map((x) => (
                  <Option value={x.id.toString()} key={x.id}>
                    {x.name}
                  </Option>
                ))}
              </Select>
            </td>
          )}
          width={150}
        />
        {isPreviewValidated && (
          <Column field='promoMaterialSize.name' title='File Size' width={80} editable={false} />
        )}
        <Column
          field='Action'
          width={80}
          cell={(gridProps: GridCellProps) => (
            <td>
              <MoreButton
                items={[
                  {
                    name: 'Delete',
                    onClick: () => handleRemove(gridProps.dataItem.uid),
                  },
                  {
                    name: 'Apply to all',
                    onClick: () => {
                      const selectedLanguageId = gridProps.dataItem.languageId
                      if (!selectedLanguageId) {
                        setToast(errorToast('Language is not selected.'))
                        return
                      }
                      const updatedFiles = promoMaterialFiles.map((file) => ({
                        ...file,
                        languageId: selectedLanguageId,
                      }))
                      setPromoMaterialFiles(updatedFiles)
                    },
                  },
                ]}
              />
            </td>
          )}
        />
      </Grid>

      <DialogFooter>
        {isPreviewValidated ? (
          <Button disabled={!isValid() || isLoading} onClick={handleUpload}>
            Upload
          </Button>
        ) : (
          <Button disabled={!isValid()} onClick={handleUploadValidation}>
            Validate
          </Button>
        )}
      </DialogFooter>
    </DialogContent>
  )
}
